import React from 'react';
import { View, StyleSheet } from 'react-native';
import { BarChart, YAxis, XAxis } from 'react-native-svg-charts';
import {
  Rect, Text as SvgText, G, Line,
} from 'react-native-svg';
import Theme from '../../theme';
import { defaultScale, graphScale } from '../../utils';

const verticalContentInset = defaultScale(10);
const horizontalContentInset = { right: defaultScale(15), left: defaultScale(15) };
const verticalGridLineMargin = defaultScale(6);

const Overlay = ({
  x, y, bandwidth, height, data, overlayValues, currentWeekIndex,
}) => data.map(({ value }, index) => {
  const { ratio, fill } = overlayValues[index];
  const offset = height - verticalContentInset - (height - verticalContentInset - y(value)) * ratio;
  const textPosition = fill === 'rgba(225,88,39,1)' ? y(value) : offset;
  return (
    <G key={`G-${index}`}>
      <Rect
        key={`rect-${index}`}
        x={x(index)}
        y={offset}
        fill={fill}
        height={height - verticalContentInset - offset}
        width={bandwidth}
        fillOpacity={0.85}
      >
        {value}
      </Rect>
      {index === currentWeekIndex && (
      <SvgText
        key={`svgtext-${index}`}
        x={x(index) + bandwidth / 2}
        y={textPosition - verticalContentInset / 2}
        fill={Theme.BLACK}
        fontSize={Theme.graphStyle.regular10.fontSize}
        fontFamily={Theme.graphStyle.regular10.fontFamily}
        alignmentBaseline='middle'
        textAnchor='middle'
      >
        this week
      </SvgText>
      )}
    </G>
  );
});

const VerticalCustomGrid = ({ x, data, bandwidth }) => {
  const lastLinePosition = x(data.length - 1) + bandwidth + verticalGridLineMargin;
  return (
    <G>
      {data.map((_, index) => (
        <Line
          key={index}
          y1='0%'
          y2='100%'
          x1={x(index) - verticalGridLineMargin}
          x2={x(index) - verticalGridLineMargin}
          stroke='rgba(0,0,0,0.1)'
        />
      ))}
      <Line
        key={lastLinePosition}
        y1='0%'
        y2='100%'
        x1={lastLinePosition}
        x2={lastLinePosition}
        stroke='rgba(0,0,0,0.1)'
      />
    </G>
  );
};

const HorizontalCustomGrid = ({ y, ticks }) => (
  <G>
    {
      ticks.map((value, index) => (
        <Line
          key={index}
          y1={y(value)}
          y2={y(value)}
          x1='4%'
          x2='6%'
          stroke='rgba(0,0,0,0.1)'
        />
      ))
}
  </G>
);

export const WeeklyWorkoutBarGraph = ({
  graphData, targetData, labels, currentWeekIndex,
}) => {
  if (!graphData || !targetData || graphData.length > 5) {
    return null;
  }

  const invisibleFill = 'rgba(0, 0, 0, 0)';
  const backgroundGraphData = graphData.map((completedMinutes, dataPointIndex) => {
    const targetMinutes = targetData[dataPointIndex];
    return {
      value: Math.max(completedMinutes, targetMinutes),
      svg: {
        fill: invisibleFill,
      },
    };
  });

  const completedWorkoutMinutes = graphData.map((completedMinutes, index) => {
    const backgroundGraphValue = backgroundGraphData[index].value;
    return {
      fill: Theme.PRIMARY,
      ratio: completedMinutes / backgroundGraphValue,
    };
  });

  const targetWorkoutMinutes = targetData.map((targetMinutes, index) => {
    const backgroundGraphValue = backgroundGraphData[index].value;
    return {
      fill: 'rgba(0, 0, 0, 0.25)',
      ratio: targetMinutes / backgroundGraphValue,
    };
  });

  return (
    <View style={{ height: graphScale(130) }}>
      <View style={{ flexDirection: 'row', flex: 1 }}>
        <YAxis
          data={backgroundGraphData}
          yAccessor={({ item }) => item.value}
          contentInset={{ bottom: verticalContentInset, top: verticalContentInset, ...horizontalContentInset }}
          svg={{
            fill: Theme.BLACK,
            fontSize: Theme.graphStyle.regular10.fontSize,
            fontFamily: Theme.graphStyle.regular10.fontFamily,
          }}
          numberOfTicks={5}
          min={0}
        />
        <View style={{ flex: 1, flexDirection: 'column' }}>
          <BarChart
            style={{ flex: 1, marginRight: defaultScale(-10) }}
            data={backgroundGraphData}
            contentInset={{ bottom: verticalContentInset, top: verticalContentInset, ...horizontalContentInset }}
            yAccessor={({ item }) => item.value}
            spacingInner={0.2}
            spacingOuter={0.2}
            numberOfTicks={5}
            gridMin={0}
          >
            <Overlay overlayValues={targetWorkoutMinutes} />
            <Overlay overlayValues={completedWorkoutMinutes} currentWeekIndex={currentWeekIndex} />
            <VerticalCustomGrid />
            <HorizontalCustomGrid />
          </BarChart>
        </View>
      </View>
      <View style={[styles.xAxis]}>
        <XAxis
          data={labels}
          contentInset={{ ...horizontalContentInset }}
          formatLabel={(_, index) => labels[index]}
          svg={{
            fontSize: Theme.graphStyle.regular10.fontSize,
            fontFamily: Theme.graphStyle.regular10.fontFamily,
            fill: Theme.BLACK,
            textAnchor: 'middle',
          }}
          style={styles.xAxisLabel}
        />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  xAxis: {
    marginLeft: defaultScale(20),
    marginTop: defaultScale(5),
  },
  xAxisLabel: {
    ...Theme.graphStyle.regular10,
    marginRight: defaultScale(-10),
    color: Theme.BLACK,
  },
});
